import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useWebSocket } from "./WebSocketContext";
import { useAuth } from "./AuthContext";
import { canAttemptWebSocketAuth } from "@/utils/wsAuth";
import { WS_CHANNELS } from "@/utils/websocket";
import { getDummyInboxItems } from "@/components/inbox/dummyData";
import type { InboxItem } from "@/components/inbox/Inbox";

interface InboxContextType {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
  incrementUnread: () => void;
  decrementUnread: () => void;
}

const InboxContext = createContext<InboxContextType | undefined>(undefined);

export const useInboxContext = () => {
  const context = useContext(InboxContext);
  if (!context) {
    throw new Error("useInboxContext must be used within InboxProvider");
  }
  return context;
};

export const InboxProvider = ({ children }: { children: ReactNode }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated, webSocketAuthReady } = useAuth();
  const { lastMessage, subscribe, unsubscribe } = useWebSocket();

  const incrementUnread = useCallback(() => {
    setUnreadCount((prev) => prev + 1);
  }, []);

  const decrementUnread = useCallback(() => {
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Load initial unread count from dummy data
  useEffect(() => {
    async function loadUnreadCount() {
      try {
        const items = await getDummyInboxItems();
        const count = items.filter(
          (item) => item.isRead === false && item.status !== "completed"
        ).length;
        setUnreadCount(count);
      } catch (err) {
        console.error("Failed to load unread count:", err);
      }
    }

    loadUnreadCount();
  }, []);

  // Subscribe to inbox WebSocket for real-time updates
  useEffect(() => {
    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) {
      return;
    }

    const channel = WS_CHANNELS.INBOX;
    subscribe(channel);

    return () => {
      unsubscribe(channel);
    };
  }, [subscribe, unsubscribe, isAuthenticated, webSocketAuthReady]);

  // Handle WebSocket messages for unread count updates
  useEffect(() => {
    const message: any = lastMessage.get(WS_CHANNELS.INBOX);

    if (message && message.full_document) {
      const itemData = message.full_document as InboxItem;

      if (message.operation === "insert") {
        // New item - increment if unread
        if (itemData.isRead === false && itemData.status !== "completed") {
          incrementUnread();
        }
      } else if (message.operation === "update") {
        // Item updated - this will be handled by the Inbox component
        // The Inbox component will call setUnreadCount with the new total
      } else if (message.operation === "delete") {
        // Item deleted - recalculate (handled by Inbox component)
      }
    }
  }, [lastMessage, incrementUnread]);

  return (
    <InboxContext.Provider
      value={{
        unreadCount,
        setUnreadCount,
        incrementUnread,
        decrementUnread,
      }}
    >
      {children}
    </InboxContext.Provider>
  );
};
